import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useSnapshot } from "valtio";
import { Modal } from "react-bootstrap";
import { engagementStore } from "src/modules/engagement/engagementStore";
import Loader from "src/components/common/loader/loader";
import { CommandGetJob } from "@services/jobs/commands/cmdGetJob";
import { EngagementService } from "@services/engagements/engagementService";
import type {
  EngagementNetworkViewResponse,
  AdGroupView,
  AudienceObjectInfo,
} from "@services/engagements/commands/cmdGetNetworkView";
import { NetworkColors } from "src/shared/enums/networkColors";
import "./networkView.scss";

/*
    Publish reads the engagement back off the ad networks. The question it
    answers is "what did and did not land, and where" — which is a matrix, not a
    tree walk. So the whole engagement is on screen at once: our objects down the
    side, networks across the top, one cell per pair. Nothing is ever "the level
    you are on", because every level is visible and tagged.
*/

// ─── Helpers ─────────────────────────────────────────────

const NETWORK_ICONS: Record<string, string> = {
  meta: "bi bi-meta",
  pinterest: "bi bi-pinterest",
  snapchat: "bi bi-snapchat",
  linkedin: "bi bi-linkedin",
  google: "bi bi-google",
  vibe: "bi bi-tv",
};

function getNetworkColor(network: string): string {
  return NetworkColors[network.toUpperCase() as keyof typeof NetworkColors] ?? "#6b7280";
}

function getNetworkIcon(network: string): string {
  return NETWORK_ICONS[network.toLowerCase()] ?? "bi bi-globe";
}

function titleCase(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

type StatusVariant = "active" | "paused" | "missing" | "error" | "unknown";

function variantOf(cell: NetworkCell | null): StatusVariant {
  if (!cell || !cell.exists) return cell?.error ? "error" : "missing";
  if (cell.error) return "error";
  const s = (cell.status ?? "").toLowerCase();
  if (s.includes("active") || s.includes("enabled")) return "active";
  if (s.includes("paused") || s.includes("disabled")) return "paused";
  return "unknown";
}

const VARIANT_LABEL: Record<StatusVariant, string> = {
  active: "Active",
  paused: "Paused",
  missing: "Not synced",
  error: "Error",
  unknown: "Unknown",
};

function cellLabel(cell: NetworkCell | null): string {
  const variant = variantOf(cell);
  if (variant === "unknown" && cell?.status) return titleCase(cell.status);
  return VARIANT_LABEL[variant];
}

function formatBudget(value: string | null): string {
  if (!value) return "—";
  const num = Number(value);
  if (isNaN(num)) return value;
  return num.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

/*
    Only abbreviate once the digits stop being readable. Below six figures the
    exact number is both shorter to scan and impossible to misread — "$33" and
    "1K" side by side invited reading the second as part of the first.
*/
function readableCount(num: number): string {
  if (Math.abs(num) >= 1_000_000) return `${trim(num / 1_000_000)}M`;
  if (Math.abs(num) >= 100_000) return `${trim(num / 1_000)}K`;
  return Math.round(num).toLocaleString("en-US");
}

function trim(num: number): string {
  return num >= 10 ? String(Math.round(num)) : num.toFixed(1).replace(/\.0$/, "");
}

function shortBudget(value: string | null | undefined): string | null {
  if (!value) return null;
  const num = Number(value);
  if (isNaN(num)) return null;
  return `$${readableCount(num)}`;
}

/*
    Audiences the network actually targets, not what our M2M thinks — the size is
    only meaningful for the includes, since excludes subtract from reach.
*/
function includedAudiences(cell: NetworkCell): AudienceObjectInfo[] {
  return (cell.audience_objects ?? []).filter((a) => a.kind === "include");
}

function audienceReach(cell: NetworkCell): number | null {
  const counts = includedAudiences(cell)
    .map((a) => a.approximate_count)
    .filter((c): c is number => c != null);
  return counts.length === 0 ? null : counts.reduce((sum, c) => sum + c, 0);
}

interface Metric {
  label: string;
  value: string;
  /** Something a human should look at, rendered amber rather than grey. */
  warn?: boolean;
  /** Nothing to report, so the slot holds its place without drawing the eye. */
  muted?: boolean;
}

/*
    Every cell at a level renders the SAME two labelled slots, in the same order,
    whether or not it has values. A dropped line would shift everything below it
    and leave neighbouring networks describing different things at the same
    height — which is what made the matrix impossible to read across.
*/
function metricsOf(row: Row, net: string, cell: NetworkCell): [Metric, Metric] {
  if (row.level === "campaign") {
    return [
      budgetMetric(cell, "Budget", row.budgetElsewhere[net] ? "per ad set" : null),
      adsMetric(row.adCoverage[net]),
    ];
  }
  if (row.level === "adgroup") {
    return [
      budgetMetric(cell, "Budget", row.budgetElsewhere[net] ? "at campaign" : null),
      audienceMetric(cell),
    ];
  }
  return [creativeMetric(cell), scheduleMetric(cell)];
}

/*
    A campaign budget and an ad-set budget are alternatives on every network we
    run, so an empty one is not a fault — it says where the money actually sits.
    Only flag it when neither level holds any.
*/
function budgetMetric(cell: NetworkCell, label: string, elsewhere: string | null): Metric {
  const budget = shortBudget(cell.budget);
  if (budget) return { label, value: budget };
  if (elsewhere) return { label, value: elsewhere, muted: true };
  return { label, value: "Not set", warn: true };
}

function adsMetric(coverage: Coverage | undefined): Metric {
  if (!coverage || coverage.total === 0) return { label: "Ads", value: "none", muted: true };
  return {
    label: "Ads",
    value: `${coverage.synced}/${coverage.total} live`,
    warn: coverage.synced < coverage.total,
  };
}

function audienceMetric(cell: NetworkCell): Metric {
  const included = includedAudiences(cell);
  if (included.length === 0) return { label: "Audience", value: "None", warn: true };
  const reach = audienceReach(cell);
  // The label stays fixed so it lines up across networks; how many audiences
  // make up that reach is a detail for the modal.
  return {
    label: "Audience",
    value: reach != null ? `${readableCount(reach)} people` : `${included.length} attached`,
  };
}

function creativeMetric(cell: NetworkCell): Metric {
  const hasCreative = !!(cell.media_url || cell.headline || cell.body_copy);
  return hasCreative
    ? { label: "Creative", value: "Ready" }
    : { label: "Creative", value: "Missing", warn: true };
}

function scheduleMetric(cell: NetworkCell): Metric {
  return cell.schedule_status
    ? { label: "Schedule", value: cell.schedule_status }
    : { label: "Schedule", value: "—", muted: true };
}

// ─── Flattened rows ──────────────────────────────────────

/*
    The three per-level network shapes share everything the matrix needs, so the
    rows carry one widened cell type and the detail panel picks out the extras.
*/
interface NetworkCell {
  exists: boolean;
  status: string | null;
  network_id: string | null;
  network_url: string | null;
  schedule_status: string | null;
  budget?: string | null;
  audience_ids?: string[];
  audience_objects?: AudienceObjectInfo[];
  headline?: string | null;
  body_copy?: string | null;
  link_description?: string | null;
  link?: string | null;
  media_url?: string | null;
  raw: Record<string, unknown> | null;
  error: { message: string } | null;
}

type Level = "campaign" | "adgroup" | "ad";

const LEVEL_LABEL: Record<Level, string> = {
  campaign: "Campaign",
  adgroup: "Ad group",
  ad: "Ad",
};

interface Row {
  /** Stable across renders and unique across levels, so it can key expansion state. */
  key: string;
  level: Level;
  depth: number;
  name: string;
  /** Second line: what this row contains, or what it targets. */
  sub: string | null;
  /** Rows nested under this one, for the collapse toggle. */
  childKeys: string[];
  cells: Record<string, NetworkCell | null>;
  /*
      Per network: does the *other* budgeted level hold the money? A campaign
      whose ad sets are funded is not misconfigured, and neither is the reverse,
      so this is what keeps the cell from crying "Not set".
  */
  budgetElsewhere: Record<string, boolean>;
  /*
      Campaign rows only: how many of the campaign's ads made it onto each
      network. This is the publish gap, and it belongs on the row you can see
      without expanding anything.
  */
  adCoverage: Record<string, Coverage>;
}

function hasBudget(cell: NetworkCell | null | undefined): boolean {
  return !!cell?.budget && Number(cell.budget) > 0;
}

function adGroupSub(group: AdGroupView): string {
  const ads = `${group.ads.length} ${group.ads.length === 1 ? "ad" : "ads"}`;
  return group.audience_name ? `${group.audience_name} · ${ads}` : ads;
}

function buildRows(data: EngagementNetworkViewResponse, networks: readonly string[]): Row[] {
  const rows: Row[] = [];
  const noBudget: Record<string, boolean> = {};
  const noCoverage: Record<string, Coverage> = {};

  for (const launch of data.launches) {
    const launchKey = `c${launch.launch_id}`;
    const groupKeys: string[] = [];
    const allAds = launch.ad_groups.flatMap((g) => g.ads);

    // Campaign: funded if any of its ad sets is. Ad set: funded if the campaign is.
    const groupsFunded: Record<string, boolean> = {};
    const launchFunded: Record<string, boolean> = {};
    const adCoverage: Record<string, Coverage> = {};
    for (const net of networks) {
      groupsFunded[net] = launch.ad_groups.some((g) => hasBudget(g.networks[net]));
      launchFunded[net] = hasBudget(launch.networks[net]);
      const synced = allAds.filter((ad) => ad.networks[net]?.exists).length;
      adCoverage[net] = { synced, total: allAds.length, problems: allAds.length - synced };
    }

    rows.push({
      key: launchKey,
      level: "campaign",
      depth: 0,
      name: launch.launch_name,
      sub: `${launch.ad_groups.length} ${launch.ad_groups.length === 1 ? "ad group" : "ad groups"}`,
      childKeys: groupKeys,
      cells: launch.networks,
      budgetElsewhere: groupsFunded,
      adCoverage,
    });

    for (const group of launch.ad_groups) {
      const groupKey = `g${group.ad_group_id}`;
      const adKeys: string[] = [];
      groupKeys.push(groupKey);
      rows.push({
        key: groupKey,
        level: "adgroup",
        depth: 1,
        name: group.ad_group_name,
        sub: adGroupSub(group),
        childKeys: adKeys,
        cells: group.networks,
        budgetElsewhere: launchFunded,
        adCoverage: noCoverage,
      });

      for (const ad of group.ads) {
        const adKey = `a${ad.ad_id}`;
        adKeys.push(adKey);
        groupKeys.push(adKey);
        rows.push({
          key: adKey,
          level: "ad",
          depth: 2,
          name: ad.ad_name,
          sub: null,
          childKeys: [],
          cells: ad.networks,
          budgetElsewhere: noBudget,
          adCoverage: noCoverage,
        });
      }
    }
  }

  return rows;
}

/** Rows a collapsed ancestor hides. Campaigns own their ads transitively. */
function hiddenKeys(rows: readonly Row[], collapsed: ReadonlySet<string>): Set<string> {
  const hidden = new Set<string>();
  for (const row of rows) {
    if (!collapsed.has(row.key)) continue;
    for (const child of row.childKeys) hidden.add(child);
  }
  return hidden;
}

// ─── Per-network coverage, shown in the column head ──────

interface Coverage {
  synced: number;
  total: number;
  problems: number;
}

function coverageByNetwork(rows: readonly Row[], networks: readonly string[]): Record<string, Coverage> {
  const out: Record<string, Coverage> = {};
  for (const net of networks) {
    let synced = 0;
    let problems = 0;
    for (const row of rows) {
      const variant = variantOf(row.cells[net] ?? null);
      if (variant === "error") problems++;
      else if (variant === "missing") problems++;
      else synced++;
    }
    out[net] = { synced, total: rows.length, problems };
  }
  return out;
}

// ─── Cells ───────────────────────────────────────────────

function Cell({
  row,
  net,
  onOpen,
}: {
  row: Row;
  net: string;
  onOpen: () => void;
}) {
  const cell = row.cells[net] ?? null;
  const variant = variantOf(cell);
  const clickable = variant !== "missing";
  const metrics = cell && clickable ? metricsOf(row, net, cell) : [];

  return (
    <button
      type="button"
      className={`nv-cell nv-cell--${variant}`}
      onClick={clickable ? onOpen : undefined}
      disabled={!clickable}
      title={
        clickable
          ? `Open what ${cellLabel(cell)} means on this network`
          : "This object does not exist on this network"
      }
    >
      <span className="nv-cell__status">
        <span className={`nv-cell__dot nv-cell__dot--${variant}`} />
        {cellLabel(cell)}
        {clickable && <i className="bi bi-chevron-right nv-cell__chev" />}
      </span>
      {metrics.length > 0 && (
        <span className="nv-cell__metrics">
          {metrics.map((metric) => (
            <span
              key={metric.label}
              className={`nv-cell__metric${metric.warn ? " nv-cell__metric--warn" : ""}${
                metric.muted ? " nv-cell__metric--muted" : ""
              }`}
            >
              <span className="nv-cell__metric-label">{metric.label}</span>
              <span className="nv-cell__metric-value">{metric.value}</span>
            </span>
          ))}
        </span>
      )}
    </button>
  );
}

// ─── Detail modal ────────────────────────────────────────

function Field({ label, value, tone }: { label: string; value: string | null; tone?: string }) {
  return (
    <div className="nv-field">
      <span className="nv-field__label">{label}</span>
      <span className="nv-field__value" style={tone ? { color: tone, fontWeight: 600 } : undefined}>
        {value || "—"}
      </span>
    </div>
  );
}

function AudienceObjects({
  objects,
  linked,
  reach,
}: {
  objects: AudienceObjectInfo[];
  linked: number;
  reach: number | null;
}) {
  return (
    <div className="nv-sub">
      <div className="nv-sub__head">
        Targeting
        <span className="nv-sub__note">
          {linked} linked here · {objects.length} in the network's spec
          {reach != null && ` · ${reach.toLocaleString()} reachable`}
        </span>
      </div>
      {objects.length === 0 ? (
        <div className="nv-sub__empty">This ad group targets no audiences on the network.</div>
      ) : (
        objects.map((object, index) => (
          <div className="nv-aud" key={`${object.audience_id ?? "unknown"}-${index}`}>
            <div className="nv-aud__head">
              <span className={`nv-aud__kind nv-aud__kind--${object.kind}`}>
                {object.kind === "exclude" ? "EXCLUDE" : "INCLUDE"}
              </span>
              <span className="nv-aud__id">{object.audience_id ?? "—"}</span>
            </div>
            {object.error ? (
              <div className="nv-aud__error">{object.error.message}</div>
            ) : (
              <>
                <Field label="Network status" value={object.network_status} />
                <Field
                  label="Approx. size"
                  value={
                    object.approximate_count != null ? object.approximate_count.toLocaleString() : null
                  }
                />
              </>
            )}
          </div>
        ))
      )}
    </div>
  );
}

function CellDetailModal({
  row,
  network,
  onClose,
}: {
  row: Row;
  network: string;
  onClose: () => void;
}) {
  const cell = row.cells[network] ?? null;
  const variant = variantOf(cell);
  const color = getNetworkColor(network);
  const tone = variant === "active" ? "#15803d" : variant === "paused" ? "#92400e" : undefined;

  return (
    <Modal show onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title as="div" className="nv-modal__title">
          <span className={`nv-tag nv-tag--${row.level}`}>{LEVEL_LABEL[row.level]}</span>
          <span className="nv-modal__name">{row.name}</span>
          <span className="nv-modal__on">on</span>
          <i className={getNetworkIcon(network)} style={{ color }} />
          <span className="nv-modal__net">{titleCase(network)}</span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {cell?.error && <div className="nv-modal__error">{cell.error.message}</div>}

        <Field label="Reported status" value={cell ? cellLabel(cell) : null} tone={tone} />
        <Field label="Network ID" value={cell?.network_id ?? null} />
        <Field label="Schedule" value={cell?.schedule_status ?? null} />
        {row.level !== "ad" && <Field label="Budget" value={formatBudget(cell?.budget ?? null)} />}

        {row.level === "ad" && (
          <>
            <Field label="Headline" value={cell?.headline ?? null} />
            <Field label="Body copy" value={cell?.body_copy ?? null} />
            <Field label="Link description" value={cell?.link_description ?? null} />
            <Field label="Link" value={cell?.link ?? null} />
            <Field label="Media" value={cell?.media_url ?? null} />
          </>
        )}

        {row.level === "adgroup" && (
          <AudienceObjects
            objects={cell?.audience_objects ?? []}
            linked={cell?.audience_ids?.length ?? 0}
            reach={cell ? audienceReach(cell) : null}
          />
        )}

        {cell?.raw && (
          <details className="nv-raw">
            <summary>Raw response from {titleCase(network)}</summary>
            <pre>{JSON.stringify(cell.raw, null, 2)}</pre>
          </details>
        )}
      </Modal.Body>

      <Modal.Footer>
        {cell?.network_url && (
          <a
            className="btn btn-sm btn-outline-secondary me-auto"
            href={cell.network_url}
            target="_blank"
            rel="noopener noreferrer"
          >
            <i className="bi bi-box-arrow-up-right me-1" />
            Open in {titleCase(network)} Ads Manager
          </a>
        )}
        <button type="button" className="btn btn-sm btn-light" onClick={onClose}>
          Close
        </button>
      </Modal.Footer>
    </Modal>
  );
}

// ─── Poll Network View job ───────────────────────────────

const TERMINAL_STATUSES = ["COMPLETED", "FAILED", "COMPLETED_WITH_ERRORS"];
const POLL_MS = 2000;

// ─── Main Panel ──────────────────────────────────────────

export const NetworkViewPanel = ({ refreshKey = 0 }: { refreshKey?: number }) => {
  const engSnap = useSnapshot(engagementStore);
  const [loading, setLoading] = useState(false);
  const [progressMsg, setProgressMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [problemsOnly, setProblemsOnly] = useState(false);
  const [open, setOpen] = useState<{ rowKey: string; network: string } | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const data = engSnap.networkViewCache as EngagementNetworkViewResponse | null;

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  useEffect(() => {
    if (refreshKey > 0) loadData();
  }, [refreshKey]);

  async function loadData() {
    setLoading(true);
    setError(null);
    setProgressMsg("Starting network view job...");
    stopPolling();

    try {
      const job = await new EngagementService().startNetworkView(engSnap.selectedEngagement.id);

      const cmd = new CommandGetJob();
      pollRef.current = setInterval(async () => {
        try {
          const updated = await cmd.run(job.id);
          const childCount = updated.children?.length ?? 0;
          const doneCount =
            updated.children?.filter((c: any) => TERMINAL_STATUSES.includes(c.status)).length ?? 0;
          setProgressMsg(`Fetching from networks... (${doneCount}/${childCount})`);

          if (TERMINAL_STATUSES.includes(updated.status)) {
            stopPolling();
            const result = await new EngagementService().getNetworkView(job.id);
            engagementStore.networkViewCache = result;
            engagementStore.networkViewCacheDate = new Date();
            setLoading(false);
            setProgressMsg(null);
          }
        } catch {
          stopPolling();
          setLoading(false);
          setProgressMsg(null);
          setError("Failed to fetch Network View");
        }
      }, POLL_MS);
    } catch (err) {
      setLoading(false);
      setProgressMsg(null);
      setError(err instanceof Error ? err.message : "Failed to start network view job");
    }
  }

  const networks = useMemo(() => data?.networks ?? [], [data]);
  const rows = useMemo(() => (data ? buildRows(data, data.networks) : []), [data]);
  const coverage = useMemo(() => coverageByNetwork(rows, networks), [rows, networks]);

  /*
      Ads start folded away. A campaign of twenty ads would otherwise open as a
      wall of identical cells, and the campaign row's "Ads n/N live" already says
      whether opening it is worth doing.
  */
  const seededFor = useRef<string | null>(null);
  useEffect(() => {
    if (!data || seededFor.current === data.generated_at) return;
    seededFor.current = data.generated_at;
    setCollapsed(
      new Set(rows.filter((r) => r.level === "adgroup" && r.childKeys.length > 0).map((r) => r.key)),
    );
  }, [data, rows]);

  /*
      Collapsed subtrees drop out; the problems filter keeps only rows that are
      missing or erroring somewhere, which is the reason anyone opens this page.
  */
  const visibleRows = useMemo(() => {
    const hidden = hiddenKeys(rows, collapsed);
    return rows.filter((row) => {
      if (hidden.has(row.key)) return false;
      if (!problemsOnly) return true;
      return networks.some((net) => {
        const variant = variantOf(row.cells[net] ?? null);
        return variant === "missing" || variant === "error";
      });
    });
  }, [rows, collapsed, problemsOnly, networks]);

  const toggle = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const openRow = open ? rows.find((r) => r.key === open.rowKey) ?? null : null;

  if (loading) {
    return (
      <div className="nv-state">
        <Loader />
        <span className="nv-state__copy">{progressMsg ?? "Fetching data from ad networks..."}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="nv-empty">
        <div className="nv-empty__title">Network view failed</div>
        <div className="nv-empty__body">{error}</div>
        <button type="button" className="btn btn-sm btn-outline-primary" onClick={loadData}>
          Retry
        </button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="nv-empty">
        <div className="nv-empty__title">Nothing fetched yet</div>
        <div className="nv-empty__body">
          This reads every campaign, ad group, and ad back off the ad networks as they exist there
          right now, and lines it up against what we built.
        </div>
        <button type="button" className="btn btn-sm btn-outline-primary" onClick={loadData}>
          Fetch network view
        </button>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="nv-empty">
        <div className="nv-empty__title">Nothing published yet</div>
        <div className="nv-empty__body">
          Once this engagement has campaigns, they show up here beside what each network reports.
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="nv">
        <div className="nv__bar">
          <span className="nv__legend">
            {(["active", "paused", "error", "missing"] as StatusVariant[]).map((v) => (
              <span className="nv__legend-item" key={v}>
                <span className={`nv-cell__dot nv-cell__dot--${v}`} />
                {VARIANT_LABEL[v]}
              </span>
            ))}
          </span>
          <span className="nv__hint">Click a cell for what the network reports</span>
          <label className="nv__filter">
            <input
              type="checkbox"
              checked={problemsOnly}
              onChange={(e) => setProblemsOnly(e.target.checked)}
            />
            Only problems
          </label>
        </div>

        <div className="nv__scroll">
          <div
            className="nv__table"
            style={{ gridTemplateColumns: `minmax(220px, 1fr) repeat(${networks.length}, 186px)` }}
          >
            <div className="nv__corner">{data.engagement_name}</div>
            {networks.map((net) => {
              const c = coverage[net];
              return (
                <div className="nv__col" key={net}>
                  <div className="nv__col-name">
                    <i className={getNetworkIcon(net)} style={{ color: getNetworkColor(net) }} />
                    {titleCase(net)}
                  </div>
                  <div className={`nv__col-cov${c.problems > 0 ? " nv__col-cov--warn" : ""}`}>
                    {c.synced}/{c.total} there
                  </div>
                </div>
              );
            })}

            {visibleRows.map((row) => {
              const isCollapsed = collapsed.has(row.key);
              const canCollapse = row.childKeys.length > 0;
              return (
                <div className="nv__rowgroup" key={row.key} style={{ display: "contents" }}>
                  <div className={`nv-name nv-name--d${row.depth}`}>
                    {canCollapse ? (
                      <button
                        type="button"
                        className="nv-name__toggle"
                        onClick={() => toggle(row.key)}
                        aria-label={isCollapsed ? "Expand" : "Collapse"}
                      >
                        <i className={`bi bi-chevron-${isCollapsed ? "right" : "down"}`} />
                      </button>
                    ) : (
                      <span className="nv-name__toggle nv-name__toggle--empty" />
                    )}
                    <span className={`nv-tag nv-tag--${row.level}`}>{LEVEL_LABEL[row.level]}</span>
                    <span className="nv-name__text" title={row.name}>
                      {row.name}
                    </span>
                    {row.sub && <span className="nv-name__sub">{row.sub}</span>}
                  </div>
                  {networks.map((net) => (
                    <div className="nv__cellwrap" key={net}>
                      <Cell
                        row={row}
                        net={net}
                        onOpen={() => setOpen({ rowKey: row.key, network: net })}
                      />
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>

        {visibleRows.length === 0 && (
          <div className="nv__filtered">Every object landed on every network.</div>
        )}
      </div>

      {open && openRow && (
        <CellDetailModal row={openRow} network={open.network} onClose={() => setOpen(null)} />
      )}
    </>
  );
};
